// src/pages/epl/innovatech/InnovaTechHome.jsx
// 🚀 InnovaTech - Dashboard principal del laboratorio
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Rocket, 
  Users, 
  User, 
  ChevronRight, 
  Clock, 
  CheckCircle, 
  Play,
  Target,
  Lightbulb,
  BarChart3,
  Shuffle,
  Award
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import useLabPersistence from '../../../hooks/useLabPersistence';

// Silenciar logs de EPL por defecto (no afecta a Vertex).
// Activar explícitamente con: VITE_EPL_VERBOSE=true
const _eplConsole = globalThis.console;
// eslint-disable-next-line no-shadow
const console = import.meta.env.VITE_EPL_VERBOSE === 'true'
  ? _eplConsole
  : { ..._eplConsole, log: () => {}, info: () => {}, warn: () => {} };

const InnovaTechHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedMode, setSelectedMode] = useState(null);
  const [showModeModal, setShowModeModal] = useState(false);
  const [labProgress, setLabProgress] = useState({});
  
  // 💾 Hook de persistencia para obtener progreso real (usar key consistente)
  const labPersistence = useLabPersistence('lab_1');

  // Configuración de laboratorios
  const labs = [
    {
      id: 'lab1',
      title: 'Lean Startup Bootcamp',
      description: 'Mapea tu hipótesis central y el circuito Build–Measure–Learn',
      mode: 'individual',
      icon: Lightbulb,
      color: 'from-green-500 to-emerald-600',
      estimatedTime: '90 min',
      modules: ['BML', 'Hipótesis', 'Experimento', 'Métrica', 'Aprendizaje']
    },
    {
      id: 'lab2', 
      title: 'Descubrimiento del Cliente',
      description: 'Valida hipótesis cliente–problema mediante entrevistas simuladas',
      mode: 'individual',
      icon: Target,
      color: 'from-teal-500 to-green-600',
      estimatedTime: '120 min',
      modules: ['Perfil cliente', 'Guion entrevista', 'Simulación IA', 'Análisis respuestas']
    },
    {
      id: 'lab3',
      title: 'Cliente–Solución / Pitch MVP', 
      description: 'Construye pitch y define métricas de validación',
      mode: 'grupal',
      icon: BarChart3,
      color: 'from-emerald-500 to-teal-600',
      estimatedTime: '150 min',
      modules: ['Selección formato', 'Guía contenido', 'Definir métricas', 'Simulación tráfico']
    },
    {
      id: 'lab4',
      title: 'Concierge MVP + Pivot',
      description: 'Ejecuta prueba manual y decide pivot o perseverar',
      mode: 'grupal', 
      icon: Shuffle,
      color: 'from-green-600 to-emerald-700',
      estimatedTime: '180 min',
      modules: ['Plan Concierge', 'Ejecución', 'Resultados', 'Decisión pivot']
    },
    {
      id: 'lab5',
      title: 'Demo Day',
      description: 'Presenta producto y evidencias a panel IA de evaluación',
      mode: 'grupal',
      icon: Award,
      color: 'from-teal-600 to-green-700', 
      estimatedTime: '120 min',
      modules: ['Checklist', 'Video Pitch', 'Panel IA', 'Feedback final']
    }
  ];

    // 🔄 Función para actualizar progreso
  const updateLabProgress = useCallback(() => {
    if (labPersistence && labPersistence.getLabProgressPercentage) {
      const lab1Progress = labPersistence.getLabProgressPercentage();
      
      // Determinar estado basado en progreso
      let status = 'not-started';
      if (lab1Progress === 100) status = 'completed';
      else if (lab1Progress > 0) status = 'in-progress';
      
      const realProgress = {
        lab1: { completion: lab1Progress, status },
        lab2: { completion: 0, status: 'not-started' },
        lab3: { completion: 0, status: 'not-started' },
        lab4: { completion: 0, status: 'not-started' },
        lab5: { completion: 0, status: 'not-started' }
      };
      
      // Solo loggear si hay cambio real para evitar spam
      setLabProgress(prev => {
        const hasChanged = !prev || prev.lab1.completion !== lab1Progress;
        if (hasChanged) {
          console.log('[🏠 DASHBOARD] Actualizando progreso Lab 1:', lab1Progress + '%', status);
        }
        return realProgress;
      });
    }
  }, []); // ← SIN DEPENDENCIAS para evitar loop infinito

  useEffect(() => {
    // 💾 Cargar progreso inicial SOLO UNA VEZ
    updateLabProgress();
    
    // 👂 Escuchar cambios en localStorage (desde otras pestañas/ventanas)
    const handleStorageChange = (event) => {
      if (event.key && event.key.includes('labProgress')) {
        console.log('[🏠 DASHBOARD] Cambio en localStorage detectado - actualizando progreso');
        setTimeout(updateLabProgress, 100);
      }
    };
    
    // 👂 Escuchar cuando se regresa a la página (actualizar progreso)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[🏠 DASHBOARD] Página visible - actualizando progreso');
        setTimeout(updateLabProgress, 200);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // ← SIN DEPENDENCIAS - solo se ejecuta al montar el componente

  const handleLabClick = (lab) => {
    // Si es primera vez, mostrar selector de modo
    if (!selectedMode && lab.mode === 'grupal') {
      setShowModeModal(true);
      return;
    }
    
    // Navegar al laboratorio
    navigate(`/dashboard/innova-tech/lab/${lab.id}`);
  };

  const handleModeSelection = (mode) => {
    setSelectedMode(mode);
    setShowModeModal(false);
    
    if (mode === 'team') {
      // Redirigir a selector de equipo
      navigate('/dashboard/innova-tech/team-selector');
    } else {
      // Continuar en modo individual
      navigate('/dashboard/innova-tech/lab/lab1');
    }
  };

  const getProgressColor = (completion) => {
    if (completion === 0) return 'bg-gray-200';
    if (completion < 50) return 'bg-yellow-400';
    if (completion < 100) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStatusIcon = (status, completion) => {
    if (completion === 100) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (status === 'in-progress') return <Play className="w-5 h-5 text-blue-600" />;
    return <Clock className="w-5 h-5 text-gray-400" />;
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="p-3 bg-gradient-to-br from-teal-500 to-green-600 rounded-xl">
            <Rocket className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">InnovaTech Lab</h1>
            <p className="text-teal-600 font-medium">Construye como emprendedor</p>
          </div>
        </div>
        
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Valida soluciones tecnológicas reales mediante 5 laboratorios prácticos. 
          Desde la hipótesis inicial hasta el Demo Day final.
        </p>
      </div>

      {/* Progress Overview */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-xl font-semibold mb-4">Tu Progreso</h2>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {labs.map((lab, index) => {
            const progress = labProgress[lab.id] || { completion: 0, status: 'not-started' };
            
            return (
              <div key={lab.id} className="text-center">
                <div className="relative">
                  <div className="w-16 h-16 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
                    <lab.icon className={`w-8 h-8 ${progress.completion > 0 ? 'text-teal-600' : 'text-gray-400'}`} />
                  </div>
                  
                  {/* Progress ring */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full border-4 border-gray-200">
                      <div 
                        className={`w-full h-full rounded-full border-4 border-transparent ${getProgressColor(progress.completion)}`}
                        style={{
                          background: `conic-gradient(from 0deg, currentColor ${progress.completion * 3.6}deg, transparent ${progress.completion * 3.6}deg)`
                        }}
                      />
                    </div>
                  </div>
                </div>
                
                <p className="text-sm font-medium text-gray-900">Lab {index + 1}</p>
                <p className="text-xs text-gray-500">{progress.completion}%</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Laboratorios */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">Laboratorios Disponibles</h2>
        
        <div className="grid gap-6">
          {labs.map((lab, index) => {
            const progress = labProgress[lab.id] || { completion: 0, status: 'not-started' };
            const IconComponent = lab.icon;
            
            return (
              <div
                key={lab.id}
                onClick={() => handleLabClick(lab)}
                className="bg-white rounded-xl border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Icon */}
                      <div className={`p-3 bg-gradient-to-br ${lab.color} rounded-xl`}>
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            Lab {index + 1}: {lab.title}
                          </h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                            lab.mode === 'individual' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {lab.mode === 'individual' ? 'Individual' : 'Grupal'}
                          </span>
                        </div>
                        
                        <p className="text-gray-600">{lab.description}</p>
                        
                        {/* Modules */}
                        <div className="flex flex-wrap gap-2 mt-3">
                          {lab.modules.map((module, idx) => (
                            <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-md">
                              {module}
                            </span>
                          ))}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-3">
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {lab.estimatedTime}
                          </span>
                          <span className="flex items-center gap-1">
                            {getStatusIcon(progress.status, progress.completion)}
                            {progress.status === 'not-started' ? 'No iniciado' : 
                             progress.completion === 100 ? 'Completado' : 'En progreso'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress & Action */}
                    <div className="flex flex-col items-end gap-3">
                      <div className="text-right">
                        <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${getProgressColor(progress.completion)} transition-all duration-300`}
                            style={{ width: `${progress.completion}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{progress.completion}% completado</p>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mode Selection Modal */}
      {showModeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-semibold text-center">Seleccionar Modo</h3>
            <p className="text-gray-600 text-center">
              ¿Cómo te gustaría trabajar en los laboratorios grupales?
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => handleModeSelection('individual')}
                className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <User className="w-6 h-6 text-blue-600" />
                  <div className="text-left">
                    <p className="font-medium">Trabajar Individualmente</p>
                    <p className="text-sm text-gray-500">Completa todos los labs por tu cuenta</p>
                  </div>
                </div>
              </button>
              
              <button
                onClick={() => handleModeSelection('team')}
                className="w-full p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <Users className="w-6 h-6 text-purple-600" />
                  <div className="text-left">
                    <p className="font-medium">Unirme a mi Equipo</p>
                    <p className="text-sm text-gray-500">Colabora en labs grupales con compañeros</p>
                  </div>
                </div>
              </button>
            </div>
            
            <button
              onClick={() => setShowModeModal(false)}
              className="w-full py-2 text-gray-500 hover:text-gray-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InnovaTechHome;